import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../utils/api";
import DashboardLayout from "../components/layout/DashboardLayout";
import { ReportsPanel } from "./Reports";
import toast from "react-hot-toast";
import { ArrowLeft } from "lucide-react";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "recruitglance@gmail.com";
const DEFAULT_SHORTLISTED_TEMPLATE = "Thank you for applying for {job_name}. Your profile has been shortlisted for recruiter review, and the team may contact you with next steps.";

export default function Profile() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeAction, setActiveAction] = useState("upgrade");
  const [subscriptions, setSubscriptions] = useState([]);
  const [canceling, setCanceling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [activePage, setActivePage] = useState("home");
  const [scansLeft, setScansLeft] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [personalDetails, setPersonalDetails] = useState({
    first_name: "",
    last_name: "",
  });
  const [emailSettings, setEmailSettings] = useState({
    sender_name: "",
    sender_email: "",
    provider: "",
    shortlisted_template: "",
    rejected_template: "",
    send_consent_required: true,
    send_consent_at: null,
    sender_mailbox_connected: false,
    sender_mailbox_has_send_scope: false,
    sender_mailbox_connected_at: null,
    sender_mailbox_email: "",
    available_variables: [],
  });

  const isFree = user?.plan === "free";
  const isPro = user?.plan === "pro";
  const isPilot = user?.plan === "pilot";
  const isEnterprise = user?.plan === "enterprise";
  const isActive = user?.is_active;

  useLongActionNotice(canceling, "Cancel subscription", 30000);
  useLongActionNotice(deleting, "Delete account", 30000);
  useLongActionNotice(savingDetails, "Save personal details", 30000);
  useLongActionNotice(savingEmailSettings, "Save candidate email settings", 30000);

  const formatBillingDate = (dateValue) => {
    if (!dateValue || isFree || !isActive) return "Not scheduled";

    const billingDate = new Date(dateValue);
    if (Number.isNaN(billingDate.getTime())) return "Not scheduled";

    return billingDate.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDaysUntil = (dateValue) => {
    if (!dateValue) return null;

    const targetDate = new Date(dateValue);
    if (Number.isNaN(targetDate.getTime())) return null;

    const diffMs = targetDate.getTime() - Date.now();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  };

  const formatPilotDate = (dateValue) => {
    if (!dateValue) return "Starts on first login";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "Not scheduled";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ---------------- AUTH ----------------
  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading]);

  useEffect(() => {
    if (!loading && user?.plan === "pilot") {
      toast("Profile settings are disabled for pilot accounts.");
      navigate("/dashboard_recruiter", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const requestedSection = searchParams.get("section");
    const allowedSections = ["upgrade", "personal", "app_configuration", "reports", "cancel", "delete", "history"];

    if (requestedSection && allowedSections.includes(requestedSection)) {
      setActivePage(requestedSection);
      setActiveAction(requestedSection);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user) return;

    setPersonalDetails({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
    });
  }, [user]);

  // ---------------- FETCH HISTORY ----------------
  useEffect(() => {
    if (!user) return;

    const fetchScansLeft = async () => {
      try {
        const res = await apiFetch("/dashboard/my_scans/");
        const data = await res.json();

        if (res.ok) {
          setScansLeft(data.scans_left);
        }
      } catch (err) {
        console.error("Failed to load scans left", err);
      }
    };

    const fetchHistory = async () => {
      try {
        const res = await apiFetch("/subscription-history");
        const data = await res.json();
        setSubscriptions(data.subscriptions || []);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchEmailSettings = async () => {
      try {
        const res = await apiFetch("/candidate-email-settings");
        const data = await res.json();

        if (res.ok) {
          setEmailSettings({
            sender_name: data.sender_name || "",
            sender_email: data.sender_email || "",
            provider: data.provider || "",
            shortlisted_template: data.shortlisted_template || "",
            rejected_template: data.rejected_template || "",
            send_consent_required: data.send_consent_required !== false,
            send_consent_at: data.send_consent_at || null,
            sender_mailbox_connected: Boolean(data.sender_mailbox_connected),
            sender_mailbox_has_send_scope: Boolean(data.sender_mailbox_has_send_scope),
            sender_mailbox_connected_at: data.sender_mailbox_connected_at || null,
            sender_mailbox_email: data.sender_mailbox_email || "",
            available_variables: data.available_variables || [],
          });
        }
      } catch (err) {
        console.error("Failed to load candidate email settings", err);
      }
    };

    fetchScansLeft();
    fetchHistory();
    fetchEmailSettings();
  }, [user]);

  if (loading || !user) return <p className="text-center mt-20">Loading...</p>;

  // ---------------- ACTIONS ----------------
  const handleCancelSubscription = async () => {
    const daysLeft = getDaysUntil(user.subscription_next_billing);
    const billingDate = formatBillingDate(user.subscription_next_billing);
    const accessMessage =
      daysLeft !== null
        ? `You still have ${daysLeft} day${daysLeft === 1 ? "" : "s"} of paid access left. If you cancel now, you can keep using your paid features until ${billingDate}. After that date your account will move to the Free plan.`
        : "If you cancel now, you can keep using your paid features until the current Stripe billing period ends. After that your account will move to the Free plan.";

    if (!window.confirm(`${accessMessage}\n\nDo you want to schedule cancellation?`)) return;

    setCanceling(true);
    setMessage("");

    try {
      const res = await apiFetch("/cancel-subscription", { method: "POST" });
      const data = await res.json();

      if (!res.ok) {
        setMessage(data?.detail || data?.message || "Failed to cancel subscription");
        return;
      }

      setMessage(data?.message || "Subscription cancellation scheduled.");

      setUser(prev => ({
        ...prev,
        ...(data?.user || {}),
      }));

      setActiveAction("cancel");

    } catch {
      setMessage("Failed to cancel subscription");
    } finally {
      setCanceling(false);
    }
  };

const handleDeleteAccount = async () => {
    // Compose warning message
    const hasSubscription = user.plan !== "free" && user.is_active;
    const warningMessage = hasSubscription
      ? "You have an active subscription. Deleting your account will cancel your subscription, permanently delete your account, and remove all workspace data. You will not be able to log in or recover this data."
      : "This action will permanently delete your account and all workspace data. You will not be able to log in or recover this data.";

    if (!window.confirm(warningMessage)) return;

    const typedConfirmation = window.prompt(
      'Type "DELETE" to permanently delete your RecruitGlance account.'
    );

    if (typedConfirmation !== "DELETE") {
      toast.error("Account deletion cancelled.");
      return;
    }

    setDeleting(true);

    try {
      // Call backend endpoint to delete account
      const res = await apiFetch("/delete-account", { method: "DELETE" });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Delete account", data?.message));
        return;
      }

      // Clear user from context
      setUser(null);

      // Go to GoodbyePage instead of home
      window.location.href = "/bye"; 

    } catch (err) {
      console.error("Delete account failed:", err);
      toast.error(actionFailureMessage("Delete account"));
    } finally {
      setDeleting(false);
    }
  };

  const getActionState = (action, user) => {
    const isFree = user.plan === "free";
    const isPro = user.plan === "pro";
    const isEnterprise = user.plan === "enterprise";
    const isActive = user.is_active;

    switch (action) {
      case "upgrade":
        return {
          disabled: isEnterprise, // already max plan
          reason: isEnterprise ? "You already have Enterprise plan" : null,
        };

      case "personal":
        return {
          disabled: false,
          reason: null,
        };

      case "app_configuration":
        return {
          disabled: false,
          reason: null,
        };

      case "cancel":
        return {
          disabled: isFree || !isActive,
          reason: isFree
            ? "No active subscription"
            : !isActive
            ? "Account inactive"
            : null,
        };

      case "delete":
        return {
          disabled: false,
          reason: null,
        };

      default:
        return { disabled: false };
    }
  };

  const handleSubscribe = async (plan) => {
    try {
      const res = await apiFetch("/subscribe", {
        method: "POST",
        body: JSON.stringify({ plan }),
      });

      const data = await res.json();
      if (data.url) window.location.href = data.url;

      if (!res.ok) {
        toast.error(actionFailureMessage("Subscription", data?.message));
        return;
      }

      // Update user state and reset used scans
      setUser(prev => ({
        ...prev,
        plan: plan,
        used_scans: data.used_scans || 0,
        is_active: true,
      }));

    } catch (err) {
      toast.error(actionFailureMessage("Subscription"));
    }
  };

  const handlePersonalDetailsChange = (event) => {
    const { name, value } = event.target;

    setPersonalDetails(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSavePersonalDetails = async (event) => {
    event.preventDefault();

    const firstName = personalDetails.first_name.trim();
    const lastName = personalDetails.last_name.trim();

    if (!firstName || !lastName) {
      toast.error("First name and last name are required.");
      return;
    }

    setSavingDetails(true);

    try {
      const res = await apiFetch("/me", {
        method: "PUT",
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Update personal details", data?.detail));
        return;
      }

      setUser(prev => ({
        ...prev,
        ...data,
        token: prev?.token,
      }));

      toast.success("Personal details updated.");
    } catch (err) {
      toast.error(actionFailureMessage("Update personal details"));
    } finally {
      setSavingDetails(false);
    }
  };

  const handleEmailSettingsChange = (event) => {
    const { name, value } = event.target;

    setEmailSettings(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEmailSettings = async (event) => {
    event.preventDefault();

    const shortlistedTemplate = emailSettings.shortlisted_template.trim() || DEFAULT_SHORTLISTED_TEMPLATE;
    const rejectedTemplate = emailSettings.rejected_template.trim();

    if (!rejectedTemplate) {
      toast.error("Rejection email template is required.");
      return;
    }

    setSavingEmailSettings(true);

    try {
      const res = await apiFetch("/candidate-email-settings", {
        method: "PUT",
        body: JSON.stringify({
          shortlisted_template: shortlistedTemplate,
          rejected_template: rejectedTemplate,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Update candidate email settings", data?.detail));
        return;
      }

      setEmailSettings({
        sender_name: data.sender_name || "",
        sender_email: data.sender_email || "",
        provider: data.provider || "",
        shortlisted_template: data.shortlisted_template || "",
        rejected_template: data.rejected_template || "",
        send_consent_required: data.send_consent_required !== false,
        send_consent_at: data.send_consent_at || null,
        sender_mailbox_connected: Boolean(data.sender_mailbox_connected),
        sender_mailbox_has_send_scope: Boolean(data.sender_mailbox_has_send_scope),
        sender_mailbox_connected_at: data.sender_mailbox_connected_at || null,
        sender_mailbox_email: data.sender_mailbox_email || "",
        available_variables: data.available_variables || [],
      });

      toast.success("Candidate email settings updated.");
    } catch (err) {
      toast.error(actionFailureMessage("Update candidate email settings"));
    } finally {
      setSavingEmailSettings(false);
    }
  };

  // ---------------- LEFT PANEL ----------------
  const leftPanel = (
    <div className="flex flex-col gap-2">
      <h3 className="mb-3 px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
        Account Menu
      </h3>

      {[
        // { key: "home", label: "🏠 Home" },
        // { key: "history", label: "📊 Subscription History" },
        { key: "dashboard", label: "📊 Dashboard" },
        { key: "personal", label: "Personal Details" },
        { key: "app_configuration", label: "App Configuration" },
        { key: "reports", label: "Reports" },
        { key: "support", label: "Support" },
        { key: "upgrade", label: "Upgrade Account" },
        { key: "cancel", label: "Cancel Plan" },
        { key: "delete", label: "Delete Account" },
      ].map(item => {
        const sectionLabel = {
          dashboard: "Workspace",
          personal: "Account",
          upgrade: "Plan & Billing",
          delete: "Account Closure",
        }[item.key];
        const isDashboardLink = item.key === "dashboard";

        return (
          <div key={item.key} className={sectionLabel ? "mt-2 grid gap-1 first:mt-0" : "grid gap-1"}>
            {sectionLabel && (
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wide text-emerald-300/70">
                {sectionLabel}
              </p>
            )}
            <button
              onClick={() => {
                if (item.key === "dashboard") {
                  navigate("/dashboard_recruiter");
                  return;
                }

                const { disabled, reason } = getActionState(item.key, user);
                if (disabled) {
                  toast.error(reason || "This option is unavailable.");
                  return;
                }

                setActivePage(item.key);
                setActiveAction(item.key);
              }}
              title={isDashboardLink ? "Back to Dashboard" : undefined}
              className={`flex min-h-[34px] items-center rounded-md px-2.5 py-1.5 text-[13px] font-medium transition
                ${activeAction === item.key
                  ? "bg-white text-emerald-950 shadow-sm"
                  : "text-emerald-100/80 hover:bg-white/10 hover:text-white"}
              `}
            >
              {isDashboardLink ? (
                <span className="inline-flex items-center gap-2">
                  <ArrowLeft size={17} />
                  Dashboard
                </span>
              ) : item.label}
            </button>
          </div>
        );
      })}
    </div>
  );

  // ---------------- ACTION CARDS ----------------
  const actionCards = (
    <section className="grid md:grid-cols-3 gap-4">
      {[
        { key: "upgrade", icon: "🚀", title: "Upgrade Account" },
        { key: "cancel", icon: "⚠️", title: "Cancel Plan" },
        { key: "delete", icon: "🗑️", title: "Delete Account" },
      ].map(card => {
        
        const { disabled, reason } = getActionState(card.key, user);

        return (
          <div
            key={card.key}
            onClick={() => {
              if (disabled) return;
              setActiveAction(card.key);
            }}
            className={`group bg-white rounded-lg border border-slate-200 p-5 shadow-sm transition
              ${
                disabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:border-slate-300 hover:shadow-md cursor-pointer"
              }`}
          >
            <div className="text-2xl mb-3">{card.icon}</div>

            <h3 className="text-base font-semibold text-slate-950 mb-2">
              {card.title}
            </h3>

            {disabled && (
              <p className="text-xs text-red-400 mb-2">
                {reason}
              </p>
            )}

            <span className="text-sm text-slate-700 font-semibold">
              {disabled ? "Unavailable" : "Open →"}
            </span>
          </div>
        );
      })}
    </section>
  );

  // ---------------- MAIN CONTENT ----------------
  const mainContent = (
    <>
      {message && <p className="text-center text-gray-700">{message}</p>}

      {/* PERSONAL DETAILS */}
      {activeAction === "personal" && (
        <section className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-slate-950">Personal Details</h3>
            <p className="text-sm text-slate-500 mt-1">
              Update the name shown across your recruiter account.
            </p>
          </div>

          <form onSubmit={handleSavePersonalDetails} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">First name</span>
                <input
                  type="text"
                  name="first_name"
                  value={personalDetails.first_name}
                  onChange={handlePersonalDetailsChange}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-slate-700">Last name</span>
                <input
                  type="text"
                  name="last_name"
                  value={personalDetails.last_name}
                  onChange={handlePersonalDetailsChange}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                />
              </label>
            </div>

            <div>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Email</span>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={savingDetails}
              className="bg-emerald-800 text-white px-5 py-2 rounded-lg font-semibold hover:bg-emerald-900 disabled:opacity-50"
            >
              {savingDetails ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </section>
      )}

      {/* APP CONFIGURATION */}
      {activeAction === "app_configuration" && (
        <section className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              App Configuration
            </p>
            <h3 className="mt-1 text-xl font-semibold text-slate-950">Candidate Email Settings</h3>
            <p className="text-sm text-slate-500 mt-1">
              Edit the rejection template used when recruiters send candidate decision emails.
            </p>
          </div>

          <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">
            Mailbox consent and Gmail/Outlook connection happen inside the email sending workflow. This page only controls the rejection template text.
          </div>

          <form onSubmit={handleSaveEmailSettings} className="space-y-5">
            <div>
              <span className="text-sm font-medium text-slate-700">Template variables</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {(emailSettings.available_variables || []).map(variable => (
                  <span
                    key={variable}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    {variable}
                  </span>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Rejected email template</span>
              <textarea
                name="rejected_template"
                value={emailSettings.rejected_template}
                onChange={handleEmailSettingsChange}
                rows={5}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm leading-6 outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              />
            </label>

            <button
              type="submit"
              disabled={savingEmailSettings}
              className="bg-emerald-800 text-white px-5 py-2 rounded-lg font-semibold hover:bg-emerald-900 disabled:opacity-50"
            >
              {savingEmailSettings ? "Saving..." : "Save Email Settings"}
            </button>
          </form>
        </section>
      )}

      {/* REPORTS */}
      {activeAction === "reports" && <ReportsPanel />}

      {/* SUPPORT */}
      {activeAction === "support" && (
        <section className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-xl font-semibold text-slate-950">Support</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            For account, billing, candidate import, bulk scan, or ranking questions, write to:
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=RecruitGlance%20support%20request`}
            className="mt-5 inline-flex min-h-[42px] items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-100"
          >
            {SUPPORT_EMAIL}
          </a>
        </section>
      )}

      {/* UPGRADE */}
      {activeAction === "upgrade" && (
        <section className="space-y-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-2xl font-semibold text-slate-950">Recruiter Plans</h3>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                Choose the plan that fits your hiring workflow. Scans are counted as candidate-job evaluations.
              </p>
            </div>
            <span className="inline-flex w-fit rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
              Current Plan: {user.plan.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-4">

          {/* FREE */}
          {isFree && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <h3 className="text-xl font-semibold text-slate-950 mb-2">Free</h3>
            <p className="text-2xl font-bold mb-1">€0/month</p>
            <p className="text-sm text-slate-500 mb-4">For testing one CV against one job before starting a paid workflow.</p>

            <ul className="text-gray-600 text-sm space-y-2 mb-6">
              <li>✔ 5 single candidate-job scans</li>
              <li>✔ Create jobs and test one CV at a time</li>
              <li>✔ Baseline match score</li>
              <li>✔ Detected signals preview</li>
              <li>✔ Recruiter dashboard preview</li>
              <li className="line-through opacity-50">Bulk Scan</li>
              <li className="line-through opacity-50">Candidate import</li>
              <li className="line-through opacity-50">AI Talent Search</li>
              <li className="line-through opacity-50">API access</li>
            </ul>

            <button
              disabled
              className="bg-slate-100 text-slate-500 px-4 py-2 rounded-lg w-full font-semibold cursor-not-allowed"
            >
              {user.plan === "free" ? "Current Plan" : "Included by Default"}
            </button>
          </div>
          )}

          {/* BASIC */}
          {!isPro && !isEnterprise && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <h3 className="text-xl font-semibold text-slate-950 mb-2">Basic</h3>
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <span className="pb-1 text-xl font-semibold text-slate-400 line-through">€49</span>
              <span className="text-3xl font-black text-emerald-900">€39</span>
              <span className="pb-1 text-sm font-medium text-slate-500">/month</span>
            </div>
            <div className="mb-4 inline-flex items-center rounded-full border border-emerald-700 bg-emerald-950 px-3 py-1 text-xs font-black uppercase tracking-wide text-emerald-50 shadow-[0_0_20px_rgba(6,78,59,0.22)]">
              Early price
            </div>
            <p className="text-sm text-slate-500 mb-4">Built for recruiters who want structured screening without advanced search or API access.</p>

            <ul className="text-gray-600 text-sm space-y-2 mb-6">
              <li>✔ 200 candidate-job scans</li>
              <li>✔ Single CV Scan access</li>
              <li>✔ Bulk Scan and candidate import</li>
              <li>✔ Baseline match score</li>
              <li>✔ Score breakdown and criteria evidence</li>
              <li>✔ Detected and unconfirmed signals</li>
              <li>✔ Shortlist, reject, notes, rankings, and scan history</li>
              <li>✔ Candidate Re-discovery from saved applicants</li>
              <li className="line-through opacity-50">AI Talent Search</li>
              <li className="line-through opacity-50">Inbox to Shortlist automation</li>
              <li className="line-through opacity-50">Candidate decision email sending</li>
              <li className="line-through opacity-50">API access</li>
            </ul>

            <button
              onClick={() => handleSubscribe("basic")}
              disabled={user.plan === "basic"}
              className="bg-emerald-800 text-white px-4 py-2 rounded-lg w-full font-semibold hover:bg-emerald-900 disabled:opacity-50"
            >
              {user.plan === "basic" ? "Current Plan" : "Choose Basic"}
            </button>
          </div>
          )}

          {/* PRO */}
          {!isEnterprise && (
          <div className="bg-slate-950 text-white p-6 rounded-lg shadow-sm border border-slate-800">
            <h3 className="text-xl font-semibold mb-2">Pro</h3>
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <span className="pb-1 text-xl font-semibold text-slate-400 line-through">€149</span>
              <span className="text-3xl font-black text-emerald-200">€119</span>
              <span className="pb-1 text-sm font-medium text-slate-400">/month</span>
            </div>
            <div className="mb-4 inline-flex items-center rounded-full border border-emerald-500/70 bg-emerald-900 px-3 py-1 text-xs font-black uppercase tracking-wide text-emerald-50 shadow-[0_0_20px_rgba(6,78,59,0.22)]">
              Early price
            </div>
            <p className="text-sm text-slate-300 mb-4">For active recruiters who need AI-assisted search, mailbox workflows, and higher monthly capacity.</p>

            <ul className="text-sm space-y-2 mb-6">
              <li>✔ 500 candidate-job scans</li>
              <li>✔ Everything in Basic</li>
              <li>✔ Higher-volume Bulk Scan workflows</li>
              <li>✔ Top-candidate AI enrichment and interview questions</li>
              <li>✔ Inbox to Shortlist and email import workflows</li>
              <li>✔ AI Talent Search with semantic matching</li>
              <li>✔ Candidate decision email sending through connected Gmail/Outlook</li>
              <li>✔ Shortlisting and recruiter notes</li>
              <li>✔ 25 API trial calls</li>
              <li className="line-through opacity-50">Production API access</li>
            </ul>

            <button
              onClick={() => handleSubscribe("pro")}
              disabled={isPro}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg w-full font-semibold hover:bg-emerald-500 disabled:opacity-50"
            >
              {isPro ? "Current Plan" : "Upgrade to Pro"}
            </button>
          </div>
          )}

          {/* ENTERPRISE */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-emerald-200">
            <h3 className="text-xl font-semibold text-slate-950 mb-2">Enterprise</h3>
            <p className="text-2xl font-bold mb-1">Custom</p>
            <p className="text-sm text-slate-500 mb-4">For teams that need an isolated workspace, dedicated processing, production API access, and custom integrations.</p>

            <ul className="text-gray-600 text-sm space-y-2 mb-6">
              <li>✔ Custom scan volume</li>
              <li>✔ Multi-user team workspace</li>
              <li>✔ Dedicated RecruitGlance deployment</li>
              <li>✔ Isolated database and candidate storage</li>
              <li>✔ Dedicated scan and indexing queues</li>
              <li>✔ Candidate decision email sending through connected Gmail/Outlook</li>
              <li>✔ Production API access</li>
              <li>✔ Custom API rate limits</li>
              <li>✔ ATS and HR tool integrations</li>
              <li>✔ Priority support</li>
              <li>✔ Custom onboarding</li>
            </ul>

            <button
              type="button"
              onClick={() => {
                window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Enterprise%20plan%20request`;
              }}
              disabled={isEnterprise}
              className="bg-emerald-700 text-white px-4 py-2 rounded-lg w-full font-semibold hover:bg-emerald-800 disabled:opacity-50"
            >
              {isEnterprise ? "Current Plan" : "Contact Sales"}
            </button>
          </div>

        </div>
        <p className="mx-auto max-w-4xl rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-3 text-center text-sm font-semibold leading-6 text-emerald-950 shadow-sm">
          Early customer pricing is available for the first 20 paying customers and remains active while the subscription stays active. Standard prices are Basic €49/month and Pro €149/month.
        </p>

        <section className="grid gap-4 md:grid-cols-3">
          {[
            {
              title: "Faster Candidate Ranking",
              body: "Rank candidates quickly and focus recruiter time on the strongest matches.",
            },
            {
              title: "Advanced Hiring Visibility",
              body: "Track candidate quality, scan volume, shortlists, and notes from one recruiter workspace.",
            },
            {
              title: "AI-Powered Matching",
              body: "Use semantic matching, skill gaps, AI reasoning, and interview prompts to support decisions.",
            },
          ].map((item) => (
            <div key={item.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h4 className="font-semibold text-slate-950">{item.title}</h4>
              <p className="mt-2 text-sm leading-6 text-slate-500">{item.body}</p>
            </div>
          ))}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-xl font-semibold text-slate-950">Frequently Asked Questions</h3>
          <div className="mt-4 space-y-3">
            <details className="rounded-lg bg-slate-50 p-4">
              <summary className="cursor-pointer font-semibold text-slate-800">
                How many CVs can I scan per month?
              </summary>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Free includes 5 single candidate-job scans. Basic includes 200 candidate-job scans. Pro includes 500 candidate-job scans, candidate decision email sending through connected Gmail/Outlook, and 25 API trial calls. Multi-user access and production API access are available on Enterprise.
              </p>
            </details>
            <details className="rounded-lg bg-slate-50 p-4">
              <summary className="cursor-pointer font-semibold text-slate-800">
                Can I upgrade anytime?
              </summary>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Yes, you can upgrade or switch plans anytime from this Profile section.
              </p>
            </details>
            <details className="rounded-lg bg-slate-50 p-4">
              <summary className="cursor-pointer font-semibold text-slate-800">
                What payment methods are supported?
              </summary>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Payments are handled securely through Stripe using supported credit and debit cards.
              </p>
            </details>
          </div>
        </section>
        </section>
      )}

      {/* CANCEL */}
      {activeAction === "cancel" && (
        <section className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-xl font-semibold text-slate-950 mb-4">Cancel Subscription</h3>

          <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
            Cancelling your subscription does not remove your account. You can
            keep using paid features until your current billing period ends
            {user.subscription_next_billing
              ? ` on ${formatBillingDate(user.subscription_next_billing)}`
              : ""}
            . After that, your account moves to the Free plan.
          </div>

          {user.cancel_at_period_end && (
            <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-900">
              Cancellation is already scheduled. Paid access remains active
              until {formatBillingDate(user.subscription_next_billing)}.
            </div>
          )}

          <button
            onClick={handleCancelSubscription}
            disabled={canceling || isFree || user.cancel_at_period_end}
            className="bg-rose-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-rose-700 disabled:opacity-50"
          >
            {canceling
              ? "Cancelling..."
              : user.cancel_at_period_end
              ? "Cancellation Scheduled"
              : "Cancel Subscription"}
          </button>
        </section>
      )}

      {/* DELETE */}
      {activeAction === "delete" && (
        <section className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-xl font-semibold text-slate-950 mb-4">Delete Account</h3>

          <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-900">
            <p className="font-semibold">Permanent action</p>
            <p className="mt-1">
              Deleting your account removes your login, candidates, CVs, jobs,
              scans, AI results, notes, API keys, mailbox connections, and
              workspace history. You will lose access immediately and this data
              cannot be recovered.
            </p>
          </div>

          <div
            onClick={handleDeleteAccount}
            className={`group bg-white rounded-lg border border-slate-200 p-5 shadow-sm transition
              ${deleting ? "opacity-40 cursor-not-allowed" : "hover:border-rose-200 hover:shadow-md cursor-pointer"}`}
          >
            <div className="text-4xl mb-4">🗑️</div>
            <h3 className="text-base font-semibold text-slate-950 mb-2">Delete Account</h3>
            <p className="text-gray-500 text-sm mb-2">
              Permanently remove your account and all data.
            </p>
            <span className="text-red-500 font-medium">
              {deleting ? "Deleting..." : "Delete →"}
            </span>
          </div>
        </section>
      )}

      {/* HISTORY */}
      {activeAction === "history" && (
        <section className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-xl font-semibold text-slate-950 mb-4">Subscription History</h3>

          {subscriptions.length === 0 ? (
            <p className="text-gray-500">No subscriptions yet.</p>
          ) : (
            <div className="space-y-4">
              {subscriptions.map(sub => (
                <div key={sub.id} className="border border-slate-200 p-4 rounded-lg">
                  <p className="font-semibold">{sub.plan.toUpperCase()}</p>
                  <p className="text-sm text-gray-500">
                    Status: {sub.status}
                  </p>
                  <p className="text-sm text-gray-500">
                    Start: {sub.start_date}
                  </p>
                  <p className="text-sm text-gray-500">
                    Next Billing: {sub.next_billing}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  );

  // ---------------- RIGHT PANEL ----------------
  const rightPanel = (
    <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
      <h4 className="font-semibold text-slate-950 mb-3">Account Info</h4>
      <div className="space-y-2 text-sm text-slate-600">
        <p className="flex items-center justify-between gap-3">
          <span>Plan</span>
          <span className="font-semibold text-slate-900">{user.plan.toUpperCase()}</span>
        </p>
        <p className="flex items-center justify-between gap-3">
          <span>Scans left</span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-900">
            {scansLeft ?? "Loading"}
          </span>
        </p>
        {isPilot ? (
          <>
            <p className="flex items-center justify-between gap-3">
              <span>Pilot started</span>
              <span className="font-semibold text-slate-900">
                {formatPilotDate(user.pilot_started_at)}
              </span>
            </p>
            <p className="flex items-center justify-between gap-3">
              <span>Pilot ends</span>
              <span className="font-semibold text-slate-900">
                {formatPilotDate(user.pilot_expires_at)}
              </span>
            </p>
          </>
        ) : (
          <p className="flex items-center justify-between gap-3">
            <span>Next billing</span>
            <span className="font-semibold text-slate-900">
              {formatBillingDate(user.subscription_next_billing)}
            </span>
          </p>
        )}
        <p className="flex items-center justify-between gap-3">
          <span>Status</span>
          <span className={`font-semibold ${isActive ? "text-emerald-700" : "text-rose-700"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
        </p>
      </div>
    </div>
  );

  return (
    <DashboardLayout
      user={user}
      eyebrow="Account settings"
      title="Profile"
      subtitle={`Manage your profile and subscription, ${user.first_name || "User"}`}
      headerAction={
        <span className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700">
          {`Scans left: ${scansLeft ?? "Loading"}`}
        </span>
      }
      leftPanel={leftPanel}
      mainContent={mainContent}
      rightPanel={rightPanel}
    />
  );
}
